import { Component, Inject, ViewChild, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';

/**
 * Componente de diálogo para colar códigos de múltiplas frotas
 */
@Component({
  selector: 'app-colar-frotas-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule
  ],
  templateUrl: './colar-dialog.component.html',
  styleUrls: ['./colar-dialog.component.scss']
})
export class ColarDialogComponent implements OnInit {
  /** Variável para armazenar os códigos de frotas colados */
  textoFrotas = '';
  
  /** Indica se o texto contém caracteres inválidos */
  contemCaracteresInvalidos = false;
  
  /** Contador de códigos válidos */
  codigosValidosCount = 0;
  
  /** Tamanho mínimo para um código de frota válido */
  private readonly tamanhoMinimoCodigo = 3;
  
  /** Tamanho máximo para um código de frota válido */
  private readonly tamanhoMaximoCodigo = 15;
  
  /** Referência ao elemento de input do textarea */
  @ViewChild('textoInput') textoInput!: ElementRef<HTMLTextAreaElement>;

  /**
   * Construtor
   * @param dialogRef Referência para o diálogo
   * @param data Dados passados para o diálogo
   * @param snackBar Serviço para exibir mensagens temporárias
   */
  constructor(
    public dialogRef: MatDialogRef<ColarDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Inicialização do componente
   */
  ngOnInit(): void {
    // Garante que o diálogo seja fechado quando o usuário pressionar ESC
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.cancelar();
      }
    });
  }
  
  /**
   * Monitora alterações no texto para atualizar o contador de códigos válidos
   */
  atualizarContadorCodigosValidos(): void {
    if (!this.textoFrotas) {
      this.codigosValidosCount = 0;
      return;
    }
    
    // Normaliza o texto antes de calcular o número de códigos válidos
    const textoNormalizado = this.textoFrotas.replace(/\r\n|\r/g, '\n');
    
    // Separa os códigos por linhas e remove linhas em branco
    const linhas = textoNormalizado
      .split('\n')
      .map(linha => linha.trim())
      .filter(linha => linha !== '');
    
    // Filtra o texto para obter apenas os códigos com dígitos válidos
    const textoFiltrado = this.filtrarApenasNumeros(this.textoFrotas);
    const codigosValidos = this.validarCodigosFrota(textoFiltrado);
    
    this.codigosValidosCount = codigosValidos.length;
    
    // Atualiza o indicador de caracteres inválidos se houverem códigos inválidos
    this.contemCaracteresInvalidos = linhas.length > 0 && linhas.some(linha => /[^\d]/.test(linha));
  }

  /**
   * Método para limpar o conteúdo do campo de texto
   */
  limparTexto(): void {
    this.textoFrotas = '';
    this.contemCaracteresInvalidos = false;
    this.codigosValidosCount = 0;
    setTimeout(() => {
      if (this.textoInput?.nativeElement) {
        this.textoInput.nativeElement.focus();
      }
    });
  }

  /**
   * Método para cancelar a operação e fechar o diálogo
   */
  cancelar(): void {
    this.dialogRef.close();
  }

  /**
   * Método para confirmar a operação e fechar o diálogo com os códigos de frota
   */
  confirmar(): void {
    // Filtra apenas números antes de enviar
    const textoFiltrado = this.filtrarApenasNumeros(this.textoFrotas);
    const codigosFrota = this.validarCodigosFrota(textoFiltrado);
    
    if (codigosFrota.length === 0) {
      this.snackBar.open('Nenhum código de frota válido foi informado', 'Fechar', {
        duration: 3000,
        panelClass: ['snackbar-aviso']
      });
      return;
    }
    
    // Verifica se há diferença entre o texto original e o filtrado
    if (textoFiltrado.replace(/\s/g, '') !== this.textoFrotas.replace(/\s/g, '')) {
      this.snackBar.open('Caracteres inválidos foram removidos dos códigos informados', 'OK', {
        duration: 3000
      });
    }
    
    // Verifica se algum código foi removido por ser inválido
    const codigosFiltrados = textoFiltrado.split(/\r?\n/).filter(codigo => codigo.trim() !== '');
    if (codigosFrota.length < codigosFiltrados.length) {
      this.snackBar.open(`${codigosFiltrados.length - codigosFrota.length} código(s) inválido(s) foram removidos`, 'OK', {
        duration: 3000
      });
    }
    
    this.dialogRef.close(codigosFrota.join('\n'));
  }

  /**
   * Filtra o texto para manter apenas números e quebras de linha
   * @param texto Texto a ser filtrado
   * @returns Texto contendo apenas números e quebras de linha
   */
  filtrarApenasNumeros(texto: string): string {
    if (!texto) return '';
    
    // Normaliza quebras de linha para garantir consistência
    const textoNormalizado = texto.replace(/\r\n|\r/g, '\n').trim();
    
    // Divide o texto em linhas, remove espaços, e filtra
    const linhas = textoNormalizado
      .split('\n')
      .map(linha => linha.replace(/[^\d]/g, '').trim())
      .filter(linha => linha !== ''); // Remove linhas vazias
    
    return linhas.join('\n');
  }
  
  /**
   * Valida os códigos de frota de acordo com as regras de tamanho
   * @param texto Texto contendo os códigos de frota
   * @returns Array com os códigos de frota válidos
   */
  validarCodigosFrota(texto: string): string[] {
    if (!texto) return [];
    
    // Normaliza quebras de linha para garantir consistência
    const textoNormalizado = texto.replace(/\r\n|\r/g, '\n').trim();
    
    return textoNormalizado
      .split('\n')
      .map(codigo => codigo.trim())
      .filter(codigo => {
        const codigoLimpo = codigo.trim();
        return codigoLimpo !== '' && 
               codigoLimpo.length <= this.tamanhoMaximoCodigo;
      });
  }

  /**
   * Manipula eventos de colagem de texto, filtrando para manter apenas números
   * @param event Evento de colagem
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    
    if (!event.clipboardData) return;
    
    // Obtém o texto colado
    const textoColado = event.clipboardData.getData('text');
    
    // Verifica se o texto contém caracteres não numéricos (exceto quebras de linha)
    this.contemCaracteresInvalidos = /[^\d\n\r]/g.test(textoColado);
    
    // Normaliza quebras de linha primeiro
    let textoNormalizado = textoColado.replace(/\r\n|\r/g, '\n');
    
    // Garante que não há quebras de linha extras
    textoNormalizado = textoNormalizado.trim();
    
    // Filtra o texto colado para manter apenas números e quebras de linha
    const linhas = textoNormalizado
      .split('\n')
      .map(linha => linha.replace(/[^\d]/g, '').trim())
      .filter(linha => linha !== ''); // Remove linhas vazias
    
    const textoFiltrado = linhas.join('\n');
    
    // Insere o texto filtrado na posição atual do cursor
    const elemento = this.textoInput.nativeElement;
    const inicio = elemento.selectionStart || 0;
    const fim = elemento.selectionEnd || 0;
    
    // Substitui o texto selecionado pelo texto filtrado
    this.textoFrotas = 
      this.textoFrotas.substring(0, inicio) +
      textoFiltrado +
      this.textoFrotas.substring(fim);
      
    // Atualiza a posição do cursor após a colagem
    setTimeout(() => {
      const novaPosicao = inicio + textoFiltrado.length;
      elemento.selectionStart = novaPosicao;
      elemento.selectionEnd = novaPosicao;
      elemento.focus();
      
      // Atualiza o contador de códigos válidos
      this.atualizarContadorCodigosValidos();
    });
    
    // Notifica o usuário se houver caracteres inválidos
    if (this.contemCaracteresInvalidos) {
      this.snackBar.open('Caracteres não numéricos foram automaticamente filtrados', 'OK', {
        duration: 3000
      });
    }
  }

  /**
   * Valida entrada de teclado para permitir apenas números
   * @param event Evento de teclado
   */
  onKeyDown(event: KeyboardEvent): void {
    // Lista de teclas permitidas (incluindo teclas de controle)
    const teclasControle = [
      'Enter', 'Backspace', 'Delete', 'Tab', 
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Escape'
    ];
    
    // Permitir combinações com teclas de controle (Ctrl+A, Ctrl+C, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    
    // Permitir apenas dígitos e teclas de controle
    const ehDigito = /^\d$/.test(event.key);
    const ehTeclaControle = teclasControle.includes(event.key);
    
    if (!ehDigito && !ehTeclaControle) {
      event.preventDefault();
      this.contemCaracteresInvalidos = true;
      
      // Dá feedback ao usuário
      this.snackBar.open('Apenas números são permitidos', 'OK', {
        duration: 1500,
        panelClass: ['snackbar-aviso']
      });
    }
    
    // Agendar atualização do contador após mudanças no texto
    setTimeout(() => this.atualizarContadorCodigosValidos(), 0);
  }
} 